import { SlicePipe } from '@angular/common';
import { Component, OnInit, computed, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { isUndefined, sortBy } from 'lodash';
import { finalize } from 'rxjs/operators';
import { UiModule } from '../../../components/ui.module';
import { tryGetErrorMessage } from '../../../error.utils';
import { customSearchForIdAndName } from '../../../search.utils';
import { LocationGroupService } from '../../../service/location-group.service';
import { TruncatePipe } from '../../../truncate.pipe';
import { LocationGroup } from '../../../types/location-group-response';

@Component({
  selector: 'app-page-select-location-group',
  templateUrl: './page-select-location-group.component.html',
  styleUrl: './page-select-location-group.component.scss',
  standalone: true,
  imports: [UiModule, NgSelectModule, FormsModule, MatTooltipModule, TruncatePipe],
  providers: [SlicePipe],
})
export class PageSelectLocationGroupComponent implements OnInit {
  private locationGroupService = inject(LocationGroupService);

  public readonly maxNameLength = 40;

  public selected = model<LocationGroup[] | LocationGroup | null>(null);

  public multi = input<boolean>(false);
  public label = input<string>('Select Location');
  public placeholder = input<string>('Select location...');
  public autoSelectDefaultLocationGroup = input<boolean>(false);
  public autoSelectLocationGroupIds = input<string[]>(undefined);
  public showBranchName = input<boolean>(true);
  public showExternalId = input<boolean>(false);
  public disabled = input<boolean>(false);
  public defaultEmpty = input<boolean>(false);
  public overrideLocationGroups = input<LocationGroup[]>([]);

  public defaultSelected = output<LocationGroup>();

  public isLoading = signal<boolean>(false);
  public error = signal<string | undefined>(undefined);
  public locationGroups = signal<LocationGroup[]>([]);
  public customSearchFn = customSearchForIdAndName;
  public dropdownLocationGroups = computed(() => {
    return this.overrideLocationGroups().length > 0
      ? this.overrideLocationGroups()
      : sortBy(this.locationGroups(), [(lg) => !lg.is_national]);
  });
  public visibleLocationGroups = computed(() => sortBy(this.dropdownLocationGroups()));

  public ngOnInit() {
    if (!this.defaultEmpty()) {
      this.getAllLocationGroups();
    }
  }

  public isTruncated(locationGroup: LocationGroup) {
    return locationGroup.name.length > this.maxNameLength;
  }

  public fullLabel(locationGroup: LocationGroup) {
    return this.showExternalId() && locationGroup.external_id ? `${locationGroup.name} (${locationGroup.external_id})` : locationGroup.name;
  }

  public reset() {
    this.locationGroups.set([]);
    this.selected.set(null);
  }

  private getAllLocationGroups() {
    if (this.overrideLocationGroups().length > 0) return;

    this.isLoading.set(true);
    this.locationGroupService
      .getAllLocationGroups()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(
        (locationGroups) => this.setLocationGroups(locationGroups.items),
        (err) => this.error.set(tryGetErrorMessage(err)),
      );
  }

  public setLocationGroups(locations: LocationGroup[]) {
    this.locationGroups.set(locations);

    if (this.autoSelectDefaultLocationGroup()) {
      this.selectDefaultLocation();
    }

    const loadIds = this.autoSelectLocationGroupIds();
    if (!isUndefined(loadIds) && loadIds.length > 0) {
      this.selectLocations(loadIds);
    }
  }

  public selectDefaultLocation() {
    if (this.locationGroups().length === 0) return;

    this.selectLocations([this.visibleLocationGroups()[0]?.id]);

    this.defaultSelected.emit(this.visibleLocationGroups()[0]);
  }

  public selectLocations(locationIds: string[]) {
    const foundLocations = this.locationGroups().filter((b) => locationIds.includes(b.id));

    this.selected.set(this.multi() ? foundLocations : foundLocations[0]);
  }
}
